import { ThemeProvider } from '@bct/styleguide';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { WelcomeWizardMount, WhatsNewModal } from './components/onboarding';
import { DarkModeSync } from './components/DarkModeSync';
import { ThemeSync } from './components/ThemeSync';
import { Toaster } from './components/ui/toaster';
import { useToastStore } from './hooks/use-toast';
import { ApiError } from './lib/api';
import { router } from './router';

/**
 * Global mutation error handler
 *
 * Shows toast notifications for mutation errors unless:
 * - The mutation has its own onError handler (meta.skipGlobalErrorHandler)
 * - The error is a 401 (handled by api.ts redirect)
 */
const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    // Skip if mutation has custom error handling
    if (mutation.meta?.skipGlobalErrorHandler) {
      return;
    }

    // Skip 401 errors - handled by api.ts with redirect to login
    if (error instanceof ApiError && error.status === 401) {
      return;
    }

    // Show error toast
    const message = error instanceof Error ? error.message : 'An unexpected error occurred';
    useToastStore.getState().addToast({
      title: 'Error',
      description: message,
      variant: 'destructive',
    });
  },
});

/**
 * Global query error handler
 *
 * Logs query errors unless:
 * - The error is a 401 (handled by auth refresh logic)
 */
const queryCache = new QueryCache({
  onError: (error) => {
    // Don't show toast for auth errors (handled by refresh logic)
    if (error instanceof ApiError && error.status === 401) return;
    if (error.message?.includes('401')) return;
    console.error('Query error:', error.message);
  },
});

const queryClient = new QueryClient({
  mutationCache,
  queryCache,
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error) => {
        // Never retry auth errors — refresh logic in api.ts handles these
        if (error instanceof ApiError && error.status === 401) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ThemeSync />
        <DarkModeSync />
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <Toaster />
          <WhatsNewModal />
          <WelcomeWizardMount />
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
